import * as RadixCollapsible from '@radix-ui/react-collapsible';
import { cva } from 'class-variance-authority';
import { ComponentType, createContext, JSX, ReactNode, Ref } from 'react';

export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'default';

export const CollapsibleContext = createContext<{
  placement: Placement;
}>({
  placement: 'bottom',
});

export type InferProps<T> = T extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[T]
  : T extends ComponentType<infer P>
    ? P
    : object;

export type CollapsibleProps = Omit<
  RadixCollapsible.CollapsibleProps,
  'asChild' | 'color'
> & {
  children: ReactNode;
  placement?: Placement;
  ref?: Ref<HTMLDivElement>;
};

export type CollapsibleTriggerProps<
  T extends keyof JSX.IntrinsicElements | ComponentType<unknown> = 'button',
> = {
  as?: T;
  className?: string;
  children: ReactNode;
} & Omit<InferProps<T>, 'className' | 'children'>;

export type CollapsibleContentProps = Omit<
  RadixCollapsible.CollapsibleContentProps,
  'asChild' | 'color'
> & {
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
};

export const contentVariants = cva(
  'overflow-hidden transition-all duration-300 ease-in-out',
  {
    variants: {
      placement: {
        default: [
          'data-[state=closed]:opacity-0 data-[state=closed]:h-0',
          'data-[state=open]:opacity-100 data-[state=open]:h-auto',
        ],
        bottom: [
          'data-[state=closed]:opacity-0 data-[state=closed]:h-0',
          'data-[state=open]:opacity-100 data-[state=open]:h-auto',
        ],
        top: [
          'data-[state=closed]:opacity-0 data-[state=closed]:h-0',
          'data-[state=open]:opacity-100 data-[state=open]:h-auto',
        ],
        left: [
          'data-[state=closed]:opacity-0 data-[state=closed]:w-0',
          'data-[state=open]:opacity-100 data-[state=open]:w-auto',
        ],
        right: [
          'data-[state=closed]:opacity-0 data-[state=closed]:w-0',
          'data-[state=open]:opacity-100 data-[state=open]:w-auto',
        ],
      },
    },
    defaultVariants: {
      placement: 'bottom',
    },
  },
);

export const rootVariants = cva('flex', {
  variants: {
    placement: {
      default: 'flex-col justify-center',
      bottom: 'flex-col justify-center',
      top: 'flex-col-reverse justify-center',
      left: 'flex-row-reverse justify-center items-center',
      right: 'flex-row justify-center items-center',
    },
  },
  defaultVariants: {
    placement: 'bottom',
  },
});
